/**
 * ConfettiCelebration Component
 * Lightweight CSS-based confetti animation for task verification
 */
import React, { useEffect, useState } from 'react'

interface ConfettiPiece {
  id: number
  left: number
  delay: number
  duration: number
  color: string
  rotation: number
}

interface ConfettiCelebrationProps {
  /** Whether to show the confetti */
  active: boolean
  /** Duration in milliseconds before auto-hiding */
  duration?: number
  /** Number of confetti pieces */
  count?: number
  /** Callback when animation completes */
  onComplete?: () => void
}

const COLORS = [
  '#10B981', // green
  '#3B82F6', // blue
  '#F59E0B', // amber
  '#EC4899', // pink
  '#8B5CF6', // purple
  '#EF4444', // red
  '#06B6D4', // cyan
]

export function ConfettiCelebration({
  active,
  duration = 2500,
  count = 50,
  onComplete
}: ConfettiCelebrationProps) {
  const [pieces, setPieces] = useState<ConfettiPiece[]>([])
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (active) {
      // Generate confetti pieces
      const newPieces: ConfettiPiece[] = []
      for (let i = 0; i < count; i++) {
        newPieces.push({
          id: i,
          left: Math.random() * 100,
          delay: Math.random() * 0.5,
          duration: 1.5 + Math.random() * 1.5,
          color: COLORS[Math.floor(Math.random() * COLORS.length)],
          rotation: Math.random() * 360
        })
      }
      setPieces(newPieces)
      setVisible(true)

      // Hide after duration
      const timer = setTimeout(() => {
        setVisible(false)
        onComplete?.()
      }, duration)

      return () => clearTimeout(timer)
    }
  }, [active, count, duration, onComplete])

  if (!visible || pieces.length === 0) return null

  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      {pieces.map((piece) => (
        <div
          key={piece.id}
          className="absolute top-0 animate-confetti-fall"
          style={{
            left: `${piece.left}%`,
            animationDelay: `${piece.delay}s`,
            animationDuration: `${piece.duration}s`,
          }}
        >
          <div
            className="w-2 h-3 animate-confetti-spin"
            style={{
              backgroundColor: piece.color,
              transform: `rotate(${piece.rotation}deg)`,
              animationDuration: `${0.5 + Math.random() * 0.5}s`,
            }}
          />
        </div>
      ))}

      {/* Add keyframes via style tag */}
      <style>{`
        @keyframes confetti-fall {
          0% {
            transform: translateY(-10px) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }

        @keyframes confetti-spin {
          0% {
            transform: rotateY(0deg) rotateX(0deg);
          }
          100% {
            transform: rotateY(360deg) rotateX(180deg);
          }
        }

        .animate-confetti-fall {
          animation: confetti-fall linear forwards;
        }

        .animate-confetti-spin {
          animation: confetti-spin linear infinite;
        }
      `}</style>
    </div>
  )
}

/**
 * Hook to trigger confetti celebration
 */
export function useConfetti() {
  const [showConfetti, setShowConfetti] = useState(false)

  const celebrate = () => {
    setShowConfetti(true)
  }

  const onConfettiComplete = () => {
    setShowConfetti(false)
  }

  return {
    showConfetti,
    celebrate,
    onConfettiComplete,
    ConfettiComponent: () => (
      <ConfettiCelebration
        active={showConfetti}
        onComplete={onConfettiComplete}
      />
    )
  }
}

export default ConfettiCelebration
